import { tracked } from '@glimmer/tracking';
import { later } from '@ember/runloop';
import { addObserver, removeObserver } from '@ember/object/observers';
import PS from 'mewe/utils/pubsub';
import { ds } from 'mewe/stores/ds';
import { isFeatureHintValid, removeFeatureHint } from 'mewe/utils/feature-hint';
import { updateLevelBadge } from 'mewe/utils/miscellaneous-utils';
import FunctionalUtils from 'mewe/shared/functional-utils';

export default class QuestNotifications {

  @tracked questHintToast;
  @tracked questState = ds.questState;

  questHintName = 'gamification-quest-create-web3-social-media-account';

  constructor(parent) {
    this.parent = parent;

    PS.Sub('gamification.quest.completed', this.questCompleted.bind(this));
    addObserver(this, 'questState.isFetching', this.checkQuestEligibility);
    this.checkQuestEligibility();
  }

  destroy() {
    removeObserver(this, 'questState.isFetching', this.checkQuestEligibility);
  }

  checkQuestEligibility() {
    if (this.questHintToast) return;

    // check feature hint only if quest state is loaded which means user is enrolled in quests
    if (!this.questState.isFetching && this.questState.isLoaded) {
      isFeatureHintValid(this.questHintName).then(({ timesViewed }) => {
        if (timesViewed === 0) {
          later(this, () => {
            const questHintToast = {
              title: __('We have something fun!'),
              message: __('Complete quests, earn badges and compete with your community!'),
              buttonText: __('Let\'s play!'),
              questToastClick: (toast) => {
                this.parent.router.transitionTo('app.quests');
                toast.destroyToast();
              },
            };

            const toastOptions = {
              toastAddedCallback: (toast) => this.questHintToast = toast,
              toastDestroyedCallback: () => this.cleanAfterQuestHint(),
            };

            this.showQuestToast(questHintToast, toastOptions);
          }, 3000); // delayed for better UX
        }
      });
    }
  }

  showQuestToast(questHint, toastOptions = {}) {
    // if there is any dialog open then wait until it's closed so that the hint appears on "clean screen",
    // retry showing the toast every 2 seconds
    if (this.parent.dynamicDialogs.hasAnyDialogOpened) {
      later(() => this.showQuestToast(questHint), 2000);
    } else {
      FunctionalUtils.message(
        { questHint },
        null,
        null,
        Object.assign({ duration: 0 }, toastOptions) // duration: 0 required to prevent auto-close
      );
    }
  }

  questCompleted(data) {
    if (!this.parent.features.isEnabled('web_is_gamification_enabled')) return;

    let quest = this.questState.currentLevel?.quests.find((quest) => quest.questId === data.questId);

    if (quest) {
      quest.completed = true;
    }

    this.showQuestToast({
      title: data.completedLevel
        ? __('Level {number} completed!', { number: data.completedLevel })
        : __('Quest completed!'),
      message: data.message,
      buttonText: __('Show my progress'),
      showConfetti: true,
      questToastClick: (toast) => {
        this.parent.router.transitionTo('app.quests');
        toast.destroyToast();
      },
    });

    if (data.completedLevel) {
      updateLevelBadge(data.completedLevel, this.parent.account.activeUser, ds.authors[this.parent.account.activeUser.id]);
    }
  }

  destroyQuestHintToast() {
    this.questHintToast?.destroyToast();
  }

  cleanAfterQuestHint() {
    removeFeatureHint(this.questHintName);
    this.questHintToast = null;
  }

}